import React, { useState, useRef } from 'react';
import { Check } from '@phosphor-icons/react';

import { FilterValue, FilterOption } from '../types';

import { Button } from '@/components/ui/button';

interface MultiSelectDropdownProps {
  internalFilter: FilterValue;
  valueOptions: FilterOption[];
  onConfirm: (selectedValues: string[]) => void;
  onClose: () => void;
}

const getInitialValues = (value: FilterValue['value']): string[] => {
  if (Array.isArray(value)) {
    return value;
  } else if (value) {
    return [value];
  }

  return [];
};

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  internalFilter,
  valueOptions,
  onConfirm,
  onClose,
}) => {
  const [pendingValues, setPendingValues] = useState<string[]>(() => getInitialValues(internalFilter.value));
  const [announcement, setAnnouncement] = useState<string>('');
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const applyButtonRef = useRef<HTMLButtonElement>(null);

  const handleValueToggle = (valueId: string) => {
    const option = valueOptions.find((opt) => opt.id === valueId);
    const isCurrentlySelected = pendingValues.includes(valueId);

    setPendingValues((prev) => {
      const newValues = prev.includes(valueId) ? prev.filter((id) => id !== valueId) : [...prev, valueId];

      // Announce the change
      if (option) {
        const action = isCurrentlySelected ? 'deselected' : 'selected';
        setAnnouncement(`${option.label} ${action}.`);
      }

      return newValues;
    });
  };

  const handleConfirm = () => {
    onConfirm(pendingValues);
    onClose();
  };

  const isValueSelected = (valueId: string): boolean => {
    return pendingValues.includes(valueId);
  };

  const focusButton = (index: number) => {
    if (index < valueOptions.length) {
      buttonRefs.current[index]?.focus();
    } else {
      applyButtonRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = Math.min(index + 1, valueOptions.length);
        focusButton(nextIndex);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = Math.max(index - 1, 0);
        focusButton(prevIndex);
        break;
      }
      case 'Enter':
      case 'Spacebar':
        e.preventDefault();
        if (index < valueOptions.length) {
          // If the user is selecting an option
          handleValueToggle(valueOptions[index].id);
        } else {
          // If the user is selecting the apply button
          handleConfirm();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  return (
    <div className="py-1" role="listbox" aria-multiselectable="true">
      {/* Screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {valueOptions.map((option, index) => (
        <button
          key={option.id}
          ref={(el) => (buttonRefs.current[index] = el)}
          type="button"
          className="relative flex w-full text-left cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
          onClick={() => handleValueToggle(option.id)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          role="option"
          aria-selected={isValueSelected(option.id)}
        >
          {isValueSelected(option.id) && <Check size={14} className="mr-2" />}
          {!isValueSelected(option.id) && <span className="mr-2 w-3.5" />}
          {option.label}
        </button>
      ))}

      <div className="border-t border-border mt-1 pt-2 px-2 pb-2">
        <Button
          ref={applyButtonRef}
          onClick={handleConfirm}
          size="sm"
          className="w-full"
          disabled={pendingValues.length === 0}
          onKeyDown={(e) => handleKeyDown(e, valueOptions.length)}
        >
          Apply ({pendingValues.length} selected)
        </Button>
      </div>
    </div>
  );
};
